import type {
  AgentChatSessionActivityStatus,
  AgentChatSessionSummaryContract,
} from '@nest-react/domain';
import {
  List as ListIcon,
  LoaderCircle,
  MessageSquare,
  MoreHorizontal,
  PanelLeftClose,
  Plus,
} from 'lucide-react';
import {
  type Key,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { type RowComponentProps, List as VirtualList } from 'react-window';
import { useInfiniteLoader } from 'react-window-infinite-loader';
import { ThinkingOrb } from 'thinking-orbs';
import { AppImage } from '~/components/ui/AppImage';
import { DropMenu } from '~/components/dropmenu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import { Button } from '~/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui/tooltip';

import {
  AGENT_CHAT_HISTORY_LIMIT,
  AGENT_HOME_RECENT_SESSION_LIMIT,
  deleteAgentChatSession,
  renameAgentChatSession,
} from '~/repositories/agentChatSessionRepository';
import { useAgentChatSessionListEvents } from '~/repositories/agentChatSessionListEvents';
import {
  buildAgentComposerSessionKey,
  useWorkspaceAgentSessionList,
} from '~/stores/workspace';
import { useWorkspaceStore } from '~/stores/workspace/workspaceStore';
import { cn } from '~/utils';

type AgentChatHistoryProps = {
  workspaceId: string;
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
  onDrawerOpenChange?: (open: boolean) => void;
};

const sessionTitle = (
  session: AgentChatSessionSummaryContract,
  fallback: string
): string => session.title?.trim() || fallback;

const formatChatDate = (iso: string, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year:
      new Date(iso).getFullYear() === new Date().getFullYear()
        ? undefined
        : 'numeric',
  }).format(new Date(iso));

const ALL_SESSIONS_ROW_HEIGHT = 76;
const ALL_SESSIONS_LIST_DEFAULT_HEIGHT = 480;
type LiveSessionActivityStatus =
  AgentChatSessionActivityStatus | 'IDLE' | 'MISSING';

const SessionActivityIndicator = ({
  status,
  label,
}: {
  status: AgentChatSessionActivityStatus | null | undefined;
  label?: string;
}): ReactElement => {
  if (!status || !label) {
    return <span aria-hidden="true" className="size-4 shrink-0" />;
  }

  return (
    <span className="grid size-4 shrink-0 place-items-center" title={label}>
      <span className="sr-only">{label}</span>
      {status === 'AWAITING_USER' ? (
        <span
          aria-hidden="true"
          className="size-1.5 rounded-full bg-amber-500"
        />
      ) : null}
      {status === 'GENERATING' ? (
        <ThinkingOrb
          aria-hidden="true"
          size={20}
          state="composing"
          style={{ height: 16, width: 16 }}
        />
      ) : null}
      {status === 'QUEUED' ? (
        <span
          aria-hidden="true"
          className="size-2 rounded-full border border-muted-foreground/60"
        />
      ) : null}
      {status === 'UNREAD' ? (
        <span aria-hidden="true" className="size-1.5 rounded-full bg-primary" />
      ) : null}
    </span>
  );
};

type AllSessionsRowProps = {
  sessions: AgentChatSessionSummaryContract[];
  activePath: string;
  locale: string;
  defaultChatLabel: string;
  loadingMore: boolean;
  loadMoreError: boolean;
  loadingMoreLabel: string;
  retryLoadingMoreLabel: string;
  onLoadMore: () => Promise<void>;
  onSessionNavigate: () => void;
};

const getAllSessionsRowKey = (index: number, data: AllSessionsRowProps): Key =>
  data.sessions[index]?.id ?? index;

const AllSessionsRow = ({
  index,
  style,
  ariaAttributes,
  sessions,
  activePath,
  locale,
  defaultChatLabel,
  loadingMore,
  loadMoreError,
  loadingMoreLabel,
  retryLoadingMoreLabel,
  onLoadMore,
  onSessionNavigate,
}: RowComponentProps<AllSessionsRowProps>): ReactElement => {
  const session = sessions[index];
  if (!session) {
    return (
      <li
        {...ariaAttributes}
        aria-live="polite"
        className="box-border flex min-w-0 items-center justify-center px-4 py-5"
        style={style}
      >
        {loadMoreError ? (
          <button
            className="min-h-10 rounded-xl px-3 text-sm font-medium text-primary-text transition-colors hover:bg-primary-soft focus-visible:outline-2 focus-visible:outline-primary"
            onClick={() => void onLoadMore()}
            type="button"
          >
            {retryLoadingMoreLabel}
          </button>
        ) : (
          <span className="inline-flex items-center gap-2 text-sm text-muted">
            <LoaderCircle
              aria-hidden="true"
              className={cn(
                'size-4 animate-spin motion-reduce:animate-none',
                !loadingMore && 'opacity-60'
              )}
            />
            {loadingMoreLabel}
          </span>
        )}
      </li>
    );
  }

  const active = activePath === `/agent/${session.id}`;

  return (
    <li
      {...ariaAttributes}
      className="box-border min-w-0 pb-1 px-4 py-5"
      style={style}
    >
      <Link
        aria-current={active ? 'page' : undefined}
        className={cn(
          'flex h-[72px] min-w-0 w-full max-w-full items-center gap-3 overflow-hidden rounded-2xl px-3 py-2 transition focus-visible:outline-2 focus-visible:outline-primary',
          active ? 'bg-nav-active' : 'hover:bg-hover focus-visible:bg-hover'
        )}
        onClick={onSessionNavigate}
        to={`/agent/${session.id}`}
      >
        <ChatThumbnail session={session} size="large" />
        <span className="w-0 min-w-0 flex-1 overflow-hidden">
          <span className="block truncate text-sm font-medium text-foreground">
            {sessionTitle(session, defaultChatLabel)}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted">
            {formatChatDate(session.updatedAt, locale)}
          </span>
        </span>
      </Link>
    </li>
  );
};

const ChatThumbnail = ({
  session,
  size = 'small',
}: {
  session: AgentChatSessionSummaryContract;
  size?: 'small' | 'large';
}): ReactElement => {
  const previewUrl = session.previewUrl ?? session.previewImageUrl;
  const className = cn(
    'shrink-0 overflow-hidden rounded-lg bg-nav-active text-muted',
    size === 'large' ? '!size-14' : '!size-6'
  );

  if (previewUrl && session.previewMediaType === 'VIDEO') {
    return (
      <video
        key={session.id}
        aria-hidden="true"
        className={cn(className, 'object-cover')}
        muted
        playsInline
        preload="metadata"
        src={previewUrl}
        tabIndex={-1}
      />
    );
  }
  if (previewUrl) {
    return (
      <AppImage
        key={session.id}
        alt=""
        className={cn(className, 'object-cover')}
        height={size === 'large' ? 56 : 32}
        loading="lazy"
        src={previewUrl}
        width={size === 'large' ? 56 : 32}
        wrapperClassName={className}
      />
    );
  }
  return (
    <span className={cn(className, 'grid place-items-center')}>
      <MessageSquare
        aria-hidden="true"
        className={size === 'large' ? 'size-5' : 'size-4'}
      />
    </span>
  );
};

type RecentSessionRowProps = {
  session: AgentChatSessionSummaryContract;
  active: boolean;
  activityStatus: AgentChatSessionActivityStatus | null | undefined;
  activityLabel?: string;
  defaultChatLabel: string;
  onDelete: (sessionId: string) => Promise<void>;
  onNavigate?: () => void;
  onRename: (session: AgentChatSessionSummaryContract) => void;
  onSeeGenerations: (sessionId: string) => void;
};

const RecentSessionRow = ({
  session,
  active,
  activityStatus,
  activityLabel,
  defaultChatLabel,
  onDelete,
  onNavigate,
  onRename,
  onSeeGenerations,
}: RecentSessionRowProps): ReactElement => {
  const { t } = useTranslation('studio');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const deleteRequestedRef = useRef(false);
  const title = sessionTitle(session, defaultChatLabel);

  const confirmDelete = async (): Promise<void> => {
    if (deletePending) return;
    setDeletePending(true);
    setDeleteError(false);
    try {
      await onDelete(session.id);
      setDeleteOpen(false);
    } catch {
      setDeleteError(true);
    } finally {
      setDeletePending(false);
    }
  };

  return (
    <>
      <div className="group flex min-w-0 items-center rounded-xl">
        <Link
          aria-current={active ? 'page' : undefined}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-1 overflow-hidden rounded-xl p-2 text-sm transition-colors focus-visible:outline-2 focus-visible:outline-primary',
            active
              ? 'bg-nav-active font-medium text-foreground'
              : 'text-muted hover:bg-hover hover:text-foreground'
          )}
          onClick={onNavigate}
          title={title}
          to={`/agent/${session.id}`}
        >
          <SessionActivityIndicator
            label={activityLabel}
            status={activityStatus}
          />
          <ChatThumbnail session={session} />
          <span className="ml-1 min-w-0 flex-1 truncate">{title}</span>
        </Link>

        <DropMenu
          items={[
            {
              id: 'rename',
              label: t('agentUi.rename'),
              onSelect: () => onRename(session),
            },
            {
              id: 'assets',
              label: t('agentUi.seeGenerationsInAssets'),
              onSelect: () => onSeeGenerations(session.id),
            },
            {
              destructive: true,
              id: 'delete',
              label: t('agentUi.deleteSession'),
              onSelect: () => {
                deleteRequestedRef.current = true;
              },
              separatorBefore: true,
            },
          ]}
          onCloseAutoFocus={event => {
            if (!deleteRequestedRef.current) return;
            event.preventDefault();
            deleteRequestedRef.current = false;
            setDeleteOpen(true);
          }}
          trigger={
            <button
              aria-label={t('agentUi.sessionActionsFor', { title })}
              className="mr-1 grid size-8 shrink-0 touch-manipulation place-items-center rounded-lg text-muted transition-colors hover:bg-hover hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-primary sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 sm:data-[state=open]:opacity-100"
              type="button"
            >
              <MoreHorizontal aria-hidden="true" className="size-4" />
            </button>
          }
        />
      </div>
      <AlertDialog
        onOpenChange={open => {
          if (deletePending) return;
          setDeleteOpen(open);
          if (!open) setDeleteError(false);
        }}
        open={deleteOpen}
      >
        <AlertDialogContent className="rounded-2xl motion-reduce:animate-none sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('agentUi.deleteSessionConfirm', { title })}
            </AlertDialogTitle>
            <AlertDialogDescription aria-live="polite">
              {t(
                deleteError
                  ? 'agentUi.deleteSessionFailed'
                  : 'agentUi.deleteSessionDescription',
                { title }
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePending}>
              {t('agentUi.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              aria-busy={deletePending}
              disabled={deletePending}
              onClick={event => {
                event.preventDefault();
                void confirmDelete();
              }}
              variant="destructive"
            >
              {deletePending ? (
                <LoaderCircle
                  aria-hidden="true"
                  className="animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {t('agentUi.deleteSession')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export const AgentChatHistory = ({
  workspaceId,
  variant = 'desktop',
  onNavigate,
  onDrawerOpenChange,
}: AgentChatHistoryProps): ReactElement => {
  const { t, i18n } = useTranslation('studio');
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [renameTarget, setRenameTarget] =
    useState<AgentChatSessionSummaryContract | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const viewAllRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const allSessionsListRef = useRef<HTMLDivElement>(null);
  const [allSessionsListHeight, setAllSessionsListHeight] = useState(
    ALL_SESSIONS_LIST_DEFAULT_HEIGHT
  );
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const activeSessionId = useMemo(() => {
    const match = /^\/agent\/([^/]+)$/.exec(location.pathname);
    return match?.[1];
  }, [location.pathname]);
  const liveSessionActivity = useWorkspaceStore(state => {
    if (!activeSessionId) return 'MISSING';
    const composer =
      state.agentComposerByKey[
        buildAgentComposerSessionKey(workspaceId, activeSessionId)
      ];
    if (!composer || composer.sessionLoading || composer.isHydrating) {
      return 'MISSING';
    }
    const statuses = composer.activePlan?.steps.map(step => step.status) ?? [];
    if (
      statuses.includes('awaiting_confirmation') ||
      statuses.includes('approval_required')
    ) {
      return 'AWAITING_USER';
    }
    if (
      statuses.includes('generating') ||
      Object.values(composer.activePlanExecutions).some(
        execution => execution.status === 'processing'
      )
    ) {
      return 'GENERATING';
    }
    if (
      statuses.includes('queued') ||
      Object.values(composer.activePlanExecutions).some(
        execution => execution.status === 'queued'
      )
    ) {
      return 'QUEUED';
    }
    return 'IDLE';
  }) as LiveSessionActivityStatus;
  const { items: sessions, loading } = useWorkspaceAgentSessionList(
    workspaceId,
    'recent',
    {
      limit: AGENT_HOME_RECENT_SESSION_LIMIT,
    }
  );
  const {
    items: allSessions,
    loading: drawerLoading,
    loadingMore,
    loadMoreError,
    hasMore,
    loadMore,
  } = useWorkspaceAgentSessionList(workspaceId, 'all', {
    limit: AGENT_CHAT_HISTORY_LIMIT,
    enabled: false,
  });
  const fetchAllAgentSessions = useWorkspaceStore(
    state => state.fetchAllAgentSessions
  );
  const upsertWorkspaceAgentSession = useWorkspaceStore(
    state => state.upsertWorkspaceAgentSession
  );
  const removeWorkspaceAgentSession = useWorkspaceStore(
    state => state.removeWorkspaceAgentSession
  );

  const setHistoryDrawerOpen = useCallback(
    (open: boolean): void => {
      setDrawerOpen(open);
      onDrawerOpenChange?.(open);
    },
    [onDrawerOpenChange]
  );

  useAgentChatSessionListEvents(workspaceId);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    void fetchAllAgentSessions(workspaceId, AGENT_CHAT_HISTORY_LIMIT);
    closeRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setHistoryDrawerOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen, fetchAllAgentSessions, setHistoryDrawerOpen, workspaceId]);

  useEffect(() => {
    if (!drawerOpen || allSessions.length === 0) return undefined;

    const element = allSessionsListRef.current;
    if (!element) return undefined;

    const updateHeight = (): void => {
      const nextHeight = element.clientHeight;
      setAllSessionsListHeight(
        nextHeight > 0 ? nextHeight : ALL_SESSIONS_LIST_DEFAULT_HEIGHT
      );
    };

    updateHeight();
    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, [allSessions.length, drawerOpen]);

  const openDrawer = (): void => {
    setHistoryDrawerOpen(true);
  };

  const closeDrawer = useCallback((): void => {
    setHistoryDrawerOpen(false);
    requestAnimationFrame(() => viewAllRef.current?.focus());
  }, [setHistoryDrawerOpen]);

  const handleSessionNavigate = useCallback((): void => {
    closeDrawer();
    onNavigate?.();
  }, [closeDrawer, onNavigate]);

  const isAllSessionsRowLoaded = useCallback(
    (index: number): boolean => index < allSessions.length || loadMoreError,
    [allSessions.length, loadMoreError]
  );
  const loadMoreRows = useCallback(async (): Promise<void> => {
    await loadMore();
  }, [loadMore]);
  const allSessionsRowCount = allSessions.length + (hasMore ? 1 : 0);
  const onAllSessionsRowsRendered = useInfiniteLoader({
    isRowLoaded: isAllSessionsRowLoaded,
    loadMoreRows,
    minimumBatchSize: 1,
    rowCount: allSessionsRowCount,
    threshold: 3,
  });

  const allSessionsRowProps = useMemo<AllSessionsRowProps>(
    () => ({
      sessions: allSessions,
      activePath: location.pathname,
      locale,
      defaultChatLabel: t('agentUi.newChat'),
      loadingMore,
      loadMoreError,
      loadingMoreLabel: t('agentUi.loadingMoreChats'),
      retryLoadingMoreLabel: t('agentUi.retryLoadingMoreChats'),
      onLoadMore: loadMore,
      onSessionNavigate: handleSessionNavigate,
    }),
    [
      allSessions,
      location.pathname,
      locale,
      loadingMore,
      loadMoreError,
      loadMore,
      t,
      handleSessionNavigate,
    ]
  );

  const startNewChat = (): void => {
    navigate('/agent');
    closeDrawer();
    onNavigate?.();
  };

  const openRename = (session: AgentChatSessionSummaryContract): void => {
    setRenameTarget(session);
    setRenameValue(sessionTitle(session, t('agentUi.newChat')));
    setRenameError(null);
  };

  const closeRename = (): void => {
    setRenameTarget(null);
    setRenameValue('');
    setRenameSaving(false);
    setRenameError(null);
  };

  const submitRename = async (): Promise<void> => {
    if (!renameTarget) return;
    const title = renameValue.trim();
    if (!title) {
      setRenameError(t('agentUi.titleRequired'));
      return;
    }
    setRenameSaving(true);
    setRenameError(null);
    try {
      const updated = await renameAgentChatSession(
        workspaceId,
        renameTarget.id,
        title
      );
      upsertWorkspaceAgentSession(workspaceId, updated);
      closeRename();
    } catch {
      setRenameSaving(false);
      setRenameError(t('agentUi.renameFailed'));
    }
  };

  const deleteSession = async (sessionId: string): Promise<void> => {
    await deleteAgentChatSession(workspaceId, sessionId);
    removeWorkspaceAgentSession(workspaceId, sessionId);
    if (activeSessionId === sessionId) {
      navigate('/agent');
      onNavigate?.();
    }
  };

  const seeGenerations = (sessionId: string): void => {
    navigate(`/assets?sessionId=${encodeURIComponent(sessionId)}`);
    onNavigate?.();
  };

  const recentSessions = sessions.slice(0, AGENT_HOME_RECENT_SESSION_LIMIT);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col border-t border-border-subtle pt-3">
        <div className="mb-1 flex min-h-9 shrink-0 items-center justify-between px-2">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted">
            {t('agentUi.recents')}
          </p>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  aria-label={t('agentUi.newChatTooltip')}
                  className="grid size-8 touch-manipulation place-items-center rounded-lg text-muted transition hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
                  onClick={startNewChat}
                  type="button"
                >
                  <Plus aria-hidden="true" className="size-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                className="motion-reduce:animate-none"
                side="right"
                sideOffset={8}
              >
                {t('agentUi.newChatTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        <div
          aria-label={t('agentUi.recents')}
          aria-live="polite"
          className="grid min-h-0 flex-1 content-start gap-0.5 overflow-x-hidden overflow-y-auto overscroll-contain pr-1 focus-visible:outline-2 focus-visible:outline-inset focus-visible:outline-primary [scrollbar-color:var(--studio-border)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-2"
          role="region"
          tabIndex={0}
        >
          {loading ? (
            <div className="grid gap-2 px-2 py-2" role="status">
              <span className="sr-only">{t('agentUi.loadingChats')}</span>
              {[0, 1, 2].map(item => (
                <span
                  className="h-9 animate-pulse rounded-lg bg-nav-active motion-reduce:animate-none"
                  key={item}
                />
              ))}
            </div>
          ) : null}
          {!loading && recentSessions.length === 0 ? (
            <p className="px-2 py-3 text-xs leading-5 text-muted">
              {t('agentUi.noChats')}
            </p>
          ) : null}
          {!loading
            ? recentSessions.map(session => {
                const active = location.pathname === `/agent/${session.id}`;
                const activityStatus =
                  active && liveSessionActivity !== 'MISSING'
                    ? liveSessionActivity === 'IDLE'
                      ? null
                      : liveSessionActivity
                    : active && session.activityStatus === 'UNREAD'
                      ? null
                      : session.activityStatus;
                const activityLabel = activityStatus
                  ? t(
                      activityStatus === 'AWAITING_USER'
                        ? 'agentUi.sessionAwaitingUser'
                        : activityStatus === 'GENERATING'
                          ? 'agentUi.sessionGenerating'
                          : activityStatus === 'QUEUED'
                            ? 'agentUi.sessionQueued'
                            : 'agentUi.sessionUnread'
                    )
                  : undefined;
                return (
                  <RecentSessionRow
                    active={active}
                    activityLabel={activityLabel}
                    activityStatus={activityStatus}
                    defaultChatLabel={t('agentUi.newChat')}
                    key={session.id}
                    onDelete={deleteSession}
                    onNavigate={onNavigate}
                    onRename={openRename}
                    onSeeGenerations={seeGenerations}
                    session={session}
                  />
                );
              })
            : null}
        </div>

        {!loading && recentSessions.length > 0 ? (
          <button
            className="mt-1 flex min-h-10 w-full shrink-0 items-center gap-2.5 rounded-xl px-2 text-left text-sm text-muted transition hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
            onClick={openDrawer}
            ref={viewAllRef}
            type="button"
          >
            <span className="grid size-8 shrink-0 place-items-center">
              <ListIcon aria-hidden="true" className="size-4" />
            </span>
            {t('agentUi.viewAllChats')}
          </button>
        ) : null}
      </div>

      <Dialog
        onOpenChange={open => {
          if (!open) closeRename();
        }}
        open={renameTarget !== null}
      >
        <DialogContent className="sm:max-w-md" showCloseButton>
          <DialogHeader>
            <DialogTitle>{t('agentUi.renameChat')}</DialogTitle>
          </DialogHeader>
          <label>
            <span className="sr-only">{t('agentUi.chatTitle')}</span>
            <Input
              autoComplete="off"
              name="session-title"
              onChange={event => setRenameValue(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void submitRename();
                }
              }}
              placeholder={t('agentUi.chatTitle')}
              value={renameValue}
            />
          </label>
          {renameError ? (
            <p aria-live="polite" className="text-sm text-destructive">
              {renameError}
            </p>
          ) : null}
          <DialogFooter>
            <Button
              disabled={renameSaving}
              onClick={closeRename}
              type="button"
              variant="outline"
            >
              {t('agentUi.cancel')}
            </Button>
            <Button
              aria-busy={renameSaving}
              disabled={renameSaving}
              onClick={() => void submitRename()}
              type="button"
            >
              {renameSaving ? (
                <LoaderCircle
                  aria-hidden="true"
                  className="animate-spin motion-reduce:animate-none"
                />
              ) : null}
              {t('agentUi.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {drawerOpen ? (
        <aside
          aria-label={t('agentUi.allChats')}
          aria-modal="false"
          className={cn(
            'z-[65] flex flex-col overflow-hidden overscroll-contain border-border bg-surface border-r',
            variant === 'desktop'
              ? 'absolute inset-y-0 left-[252px] w-[360px] border-l'
              : 'fixed inset-0 border-r shadow-[12px_0_32px_rgb(15_23_42/0.08)]'
          )}
          role="dialog"
        >
          <div className="sticky top-0 z-10 flex h-[calc(5rem+env(safe-area-inset-top))] shrink-0 items-center justify-between border-b border-border-subtle bg-surface px-6 pt-[env(safe-area-inset-top)]">
            <h2 className="text-balance text-xl font-semibold tracking-tight text-foreground">
              {t('agentUi.allChats')}
            </h2>
            <div className="flex items-center gap-2">
              <button
                aria-label={t('agentUi.newChat')}
                className="grid size-10 place-items-center rounded-xl bg-primary text-white transition hover:bg-primary/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                onClick={startNewChat}
                type="button"
              >
                <Plus aria-hidden="true" className="size-5" />
              </button>
              <button
                aria-label={t('agentUi.closeChatHistory')}
                className="grid size-10 place-items-center rounded-xl text-muted transition hover:bg-hover hover:text-foreground focus-visible:outline-2 focus-visible:outline-primary"
                onClick={closeDrawer}
                ref={closeRef}
                type="button"
              >
                <PanelLeftClose aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>

          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            {drawerLoading && allSessions.length === 0 ? (
              <p className="px-3 py-6 text-sm text-muted" role="status">
                {t('agentUi.loadingChats')}
              </p>
            ) : null}
            {!drawerLoading && allSessions.length === 0 ? (
              <div className="px-3 py-10 text-center">
                <MessageSquare
                  aria-hidden="true"
                  className="mx-auto mb-3 size-6 text-muted"
                />
                <p className="text-sm text-muted">{t('agentUi.noChats')}</p>
              </div>
            ) : null}
            {allSessions.length > 0 ? (
              <div className="min-h-0 flex-1" ref={allSessionsListRef}>
                <VirtualList
                  className="min-w-0 w-full"
                  defaultHeight={ALL_SESSIONS_LIST_DEFAULT_HEIGHT}
                  onRowsRendered={onAllSessionsRowsRendered}
                  rowComponent={AllSessionsRow}
                  rowCount={allSessionsRowCount}
                  rowHeight={ALL_SESSIONS_ROW_HEIGHT}
                  rowKey={getAllSessionsRowKey}
                  rowProps={allSessionsRowProps}
                  style={{ height: allSessionsListHeight }}
                  tagName="ul"
                />
              </div>
            ) : null}
          </div>
        </aside>
      ) : null}
    </div>
  );
};
